import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { BarLoader, IconButton } from '@st-operations/uikit/src/v2'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1'
import { Button, BUTTON_COLOR, BUTTON_THEME } from '@st-operations/uikit/src/v2'

import {
  getOrganizationUsers,
  getOrganizationDeactivatedUsers,
  removeOrganizationUser,
} from '_services/predactiv-admin'
import Table from '../table'

const DeleteUserModal = ({ user, onClose, onConfirm }) => {
  const handleDelete = () => {
    onConfirm(user)
    setTimeout(onClose, 200)
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <h2 className="text-center text-lg font-bold mb-4">Are you sure?</h2>
      <div className="mb-4">
        You are removing <b>{user.email}</b> from this organization.
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} theme={BUTTON_THEME.GHOST}>
          Cancel
        </Button>
        <Button onClick={handleDelete} color={BUTTON_COLOR.RED}>
          Continue
        </Button>
      </div>
    </Dialog>
  )
}

const UsersManager = ({ organizationId, env }) => {
  const [users, setUsers] = useState([])
  const [deactivatedUsers, setDeactivatedUsers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [userToDelete, setUserToDelete] = useState(null)

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const [usersResponse, deactivatedUsersResponse] = await Promise.all([
        getOrganizationUsers({ env, id: organizationId }),
        getOrganizationDeactivatedUsers(organizationId, env),
      ])
      setUsers(usersResponse.users || [])
      setDeactivatedUsers(
        deactivatedUsersResponse.deactivatedUsers ||
          deactivatedUsersResponse.users ||
          deactivatedUsersResponse.items ||
          []
      )
    } catch (error) {
      console.error('Error fetching organization users:', error)
      toast.error('Failed to fetch organization users')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [organizationId, env])

  const handleRemoveUser = async (user) => {
    try {
      await removeOrganizationUser({ env, organizationId, userId: user._id, email: user.email })
      toast.success('User removed successfully')
      fetchUsers()
    } catch (error) {
      console.error('Error removing user:', error)
      toast.error('Failed to remove user')
    }
  }

  const renderAction = (data) => (
    <IconButton
      onClick={() => setUserToDelete(data)}
      aria-label="Remove User"
      iconProps={{
        icon: 'trash',
        type: 'outline',
        width: 16,
        height: 16,
        color: '#3D3C68',
      }}
    />
  )

  if (isLoading) return <BarLoader />

  return (
    <div className="apa-org__users">
      <h3>Organization Users</h3>
      <Table
        items={users.map((user) => ({
          data: user,
          values: [
            user.email,
            user.role,
            user.createdAt ? dayjs(user.createdAt).format('MMM D, YYYY HH:mm') : '—',
          ],
        }))}
        header={['Email', 'Role', 'Created At']}
        renderAction={renderAction}
        gridTemplateColumns="1fr 1fr 1fr auto"
      />
      <h3>Organization Deactivated Users</h3>
      <If condition={deactivatedUsers}>
        <Table
          items={deactivatedUsers.map((user) => ({
            data: user,
            values: [
              user.email,
              user.role,
              user.createdAt ? dayjs(user.createdAt).format('MMM D, YYYY HH:mm') : '—',
            ],
          }))}
          header={['Email', 'Role', 'Created At']}
          renderAction={() => null}
          gridTemplateColumns="1fr 1fr 1fr auto"
        />
      </If>
      <If condition={userToDelete}>
        <DeleteUserModal
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
          onConfirm={handleRemoveUser}
        />
      </If>
    </div>
  )
}

UsersManager.propTypes = {
  organizationId: PropTypes.string.isRequired,
  env: PropTypes.string.isRequired,
}

export default UsersManager
